import { FunctionComponent } from "react";

import { AutoUpdateSettings } from "../schema/caseStudies";

interface CaseStudyAutoUpdateProps {
	settings: AutoUpdateSettings;
	onSettingsChange: (settings: AutoUpdateSettings) => void;
	onTriggerManualUpdate: () => void;
}

export const CaseStudyAutoUpdate: FunctionComponent<CaseStudyAutoUpdateProps> = ({
	settings,
	onSettingsChange,
	onTriggerManualUpdate,
}) => {
	const formatDate = (dateStr: string | null): string => {
		if (!dateStr) {
			return "";
		}

		return new Date(dateStr).toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<div className="px-6 py-4 border-b border-border-light bg-brand-white">
			<div className="flex flex-wrap items-center gap-4">
				{/* Auto-update checkbox */}
				<label className="flex items-center gap-2 cursor-pointer">
					<input
						checked={settings.autoUpdateEnabled}
						className="w-4 h-4 rounded border-border-light text-brand-light focus:ring-brand-light/30 cursor-pointer"
						type="checkbox"
						onChange={(e) => onSettingsChange({ ...settings, autoUpdateEnabled: e.target.checked })}
					/>
					<span className="text-sm text-gray-700">
						Auto-update: Weekly check for new case studies from your website and/or documents
					</span>
				</label>

				{/* Divider */}
				<div className="h-6 w-px bg-gray-300 hidden lg:block" />

				{/* Website URL input */}
				<div className="flex items-center gap-2">
					<label className="text-xs font-medium text-gray-500 whitespace-nowrap" htmlFor="cs-url">
						URL:
					</label>
					<input
						className="px-3 py-1.5 text-sm border border-border-light rounded-lg w-[240px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						id="cs-url"
						placeholder="signalist.io/case-studies"
						type="text"
						value={settings.caseStudyUrl}
						onChange={(e) => onSettingsChange({ ...settings, caseStudyUrl: e.target.value })}
					/>
				</div>

				{/* Divider */}
				<div className="h-6 w-px bg-gray-300 hidden lg:block" />

				{/* Document URL input */}
				<div className="flex items-center gap-2">
					<label
						className="text-xs font-medium text-gray-500 whitespace-nowrap"
						htmlFor="cs-doc-url"
					>
						Doc URL:
					</label>
					<input
						className="px-3 py-1.5 text-sm border border-border-light rounded-lg w-[240px] focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
						id="cs-doc-url"
						placeholder="Google Drive / document URL"
						type="text"
						value={settings.caseStudyDocumentUrl}
						onChange={(e) =>
							onSettingsChange({ ...settings, caseStudyDocumentUrl: e.target.value })
						}
					/>
				</div>

				{/* Last auto-update date */}
				{settings.lastAutoUpdate && (
					<span className="text-xs text-gray-500">
						Last update: {formatDate(settings.lastAutoUpdate)}
					</span>
				)}

				{/* Trigger manual update */}
				<button
					className="ml-auto px-4 py-1.5 text-sm font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer flex items-center gap-2"
					disabled={settings.autoUpdateInProgress}
					type="button"
					onClick={onTriggerManualUpdate}
				>
					{settings.autoUpdateInProgress && (
						<div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
					)}
					{settings.autoUpdateInProgress ? "Update in Progress…" : "Trigger Manual Update Now"}
				</button>
			</div>
		</div>
	);
};
